import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/components/theme-provider';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/ui/page-loader';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ClientPortalAuthProvider } from './hooks/useClientPortalAuth';
import { Loader2 } from 'lucide-react';
import { Sentry } from '@/lib/sentry';
import { GlobalErrorFallback } from '@/components/ui/error-boundary';
import { AppSessionProvider, useAppSession } from '@/contexts/AppSessionContext';
import { RouteGroupBoundary } from '@/components/routing/RouteGroupBoundary';
import { PostHogProvider } from '@/components/providers/PostHogProvider';
import { DemoModeProvider } from '@/contexts/DemoModeContext';
import { DashboardFilterProvider } from '@/contexts/DashboardFilterContext';
import { SensitiveDataRegistryProvider } from '@/contexts/SensitiveDataRegistry';
import { BidCelebrationProvider } from '@/contexts/BidCelebrationContext';
import { useRouteTransitionInstrumentation } from '@/hooks/useRouteTransitionInstrumentation';
import { useQueryLatencyInstrumentation } from '@/hooks/useQueryLatencyInstrumentation';

// Lazy load all route components for code splitting
const Auth = lazy(() => import('./pages/Auth'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Clients = lazy(() => import('./pages/Clients'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const ClientQuestions = lazy(() => import('./pages/ClientQuestions'));
const AnalysisDetail = lazy(() => import('./pages/AnalysisDetail'));
const MeetingVideo = lazy(() => import('./pages/MeetingVideo'));
const Analyze = lazy(() => import('./pages/Analyze'));
const Contracts = lazy(() => import('./pages/Contracts'));
const AddContract = lazy(() => import('./pages/AddContract'));
const Goals = lazy(() => import('./pages/Goals'));
const BusinessDev = lazy(() => import('./pages/BusinessDev'));
const JobBidDetail = lazy(() => import('./pages/JobBidDetail'));
const ProjectsDashboard = lazy(() => import('./pages/ProjectsDashboard'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Meetings = lazy(() => import('./pages/Meetings'));
const MeetingAudit = lazy(() => import('./pages/MeetingAudit'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const Changelog = lazy(() => import('./pages/Changelog'));
const DriveImports = lazy(() => import('./pages/DriveImports'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const WeeklySummaryEditor = lazy(() => import('./pages/WeeklySummaryEditor'));
const MeetingPrepEditor = lazy(() => import('./pages/MeetingPrepEditor'));
const EmailComposer = lazy(() => import('./pages/EmailComposer'));
// TasksDashboard removed - functionality moved to ProjectDetail
const TaskDetail = lazy(() => import('./pages/TaskDetail'));
const MiniSOWs = lazy(() => import('./pages/MiniSOWs'));
const MiniSOWDetail = lazy(() => import('./pages/MiniSOWDetail'));
const MiniSOWCreate = lazy(() => import('./pages/MiniSOWCreate'));
const MiniSOWEdit = lazy(() => import('./pages/MiniSOWEdit'));
const BudgetDashboard = lazy(() => import('./pages/BudgetDashboard'));
const EmailReviews = lazy(() => import('./pages/EmailReviews'));
const ClientLogin = lazy(() => import('./pages/client/ClientLogin'));
const ClientVerify = lazy(() => import('./pages/client/ClientVerify'));
const ClientSOWView = lazy(() => import('./pages/client/ClientSOWView'));
const ClientSOWRedline = lazy(() => import('./pages/client/ClientSOWRedline'));
const ClientAgreementView = lazy(() => import('./pages/client/ClientAgreementView'));
// Contract Agreements (E-Signature)
const ContractAgreements = lazy(() => import('./pages/ContractAgreements'));
const ContractAgreementDetail = lazy(() => import('./pages/ContractAgreementDetail'));
const ContractAgreementTemplates = lazy(() => import('./pages/ContractAgreementTemplates'));
const ContractAgreementTemplateEdit = lazy(() => import('./pages/ContractAgreementTemplateEdit'));
const ContractAgreementCreate = lazy(() => import('./pages/ContractAgreementCreate'));
const AgreementVerify = lazy(() => import('./pages/AgreementVerify'));
const NotFound = lazy(() => import('./pages/NotFound'));

const queryClient = new QueryClient();

/**
 * Bridges auth state changes to React Query cache invalidation.
 * When a session token is refreshed (e.g., returning from idle), all cached
 * queries are invalidated so they refetch with the fresh auth token.
 * Without this, queries fired during the stale-token window return incomplete
 * data that persists until a manual page refresh.
 */
function AuthQueryBridge() {
  const { authEvent } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (authEvent === 'TOKEN_REFRESHED' || authEvent === 'SIGNED_IN') {
      qc.invalidateQueries();
    }
  }, [authEvent, qc]);

  return null;
}

function ProtectedAppShell() {
  const { user, loading, capabilities, isCapabilitiesLoading } = useAppSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    const currentPath = location.pathname + location.search;
    const shouldRedirect = !currentPath.startsWith('/auth') && !currentPath.startsWith('/invite');
    const authUrl =
      shouldRedirect && currentPath !== '/'
        ? `/auth?redirect=${encodeURIComponent(currentPath)}`
        : '/auth';
    return <Navigate to={authUrl} replace />;
  }

  return (
    <DashboardLayout
      capabilitiesOverride={capabilities}
      isCapabilitiesLoadingOverride={isCapabilitiesLoading}
    >
      <Outlet />
    </DashboardLayout>
  );
}

function AppRoutes() {
  const { user, loading, isRecoveryMode } = useAppSession();

  useRouteTransitionInstrumentation();
  useQueryLatencyInstrumentation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={
            user && !isRecoveryMode ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/auth"
          element={
            user && !isRecoveryMode && !localStorage.getItem('pending2FA') ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Auth />
            )
          }
        />
        <Route element={<ProtectedAppShell />}>
          <Route element={<RouteGroupBoundary routeGroup="Pipeline" />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analyze" element={<Analyze />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/meetings/audit" element={<MeetingAudit />} />
            <Route path="/tasks" element={<Navigate to="/projects" replace />} />
            <Route path="/tasks/:clickupTaskId" element={<TaskDetail />} />
            <Route path="/goals" element={<Goals />} />
            <Route path="/business-dev" element={<BusinessDev />} />
            <Route path="/business-dev/bids/:bidId" element={<JobBidDetail />} />
            <Route path="/projects" element={<ProjectsDashboard />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/financials" element={<Navigate to="/budget" replace />} />
            <Route path="/budget" element={<BudgetDashboard />} />
            <Route path="/email-reviews" element={<EmailReviews />} />
          </Route>

          <Route element={<RouteGroupBoundary routeGroup="Clients" />}>
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/clients/:id/questions" element={<ClientQuestions />} />
            <Route path="/clients/:id/analysis/:analysisId" element={<AnalysisDetail />} />
            <Route path="/clients/:id/analysis/:analysisId/video" element={<MeetingVideo />} />
            <Route path="/clients/:id/weekly-summary" element={<WeeklySummaryEditor />} />
            <Route path="/clients/:id/meeting-prep" element={<MeetingPrepEditor />} />
            <Route path="/clients/:clientId/compose" element={<EmailComposer />} />
            <Route path="/contracts" element={<Contracts />} />
            <Route path="/contracts/add" element={<AddContract />} />
            <Route path="/mini-sows" element={<MiniSOWs />} />
            <Route path="/mini-sows/create" element={<MiniSOWCreate />} />
            <Route path="/mini-sows/:id" element={<MiniSOWDetail />} />
            <Route path="/mini-sows/:id/edit" element={<MiniSOWEdit />} />
            <Route path="/contract-agreements" element={<ContractAgreements />} />
            <Route path="/contract-agreements/:id" element={<ContractAgreementDetail />} />
            <Route path="/contract-agreements/templates" element={<ContractAgreementTemplates />} />
            <Route
              path="/contract-agreements/templates/:id/edit"
              element={<ContractAgreementTemplateEdit />}
            />
            <Route path="/contract-agreements/create" element={<ContractAgreementCreate />} />
            <Route path="/imports" element={<DriveImports />} />
          </Route>

          <Route element={<RouteGroupBoundary routeGroup="Settings" />}>
            <Route path="/team" element={<Navigate to="/settings" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/changelog" element={<Changelog />} />
          </Route>
        </Route>
        {/* Public invite acceptance route - handles its own auth */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        {/* Client Portal Routes - separate auth system */}
        <Route
          path="/client/login"
          element={
            <ClientPortalAuthProvider>
              <ClientLogin />
            </ClientPortalAuthProvider>
          }
        />
        <Route
          path="/client/verify/:token"
          element={
            <ClientPortalAuthProvider>
              <ClientVerify />
            </ClientPortalAuthProvider>
          }
        />
        <Route
          path="/client/sow/:token"
          element={
            <ClientPortalAuthProvider>
              <ClientSOWView />
            </ClientPortalAuthProvider>
          }
        />
        <Route
          path="/client/sow/:token/edit"
          element={
            <ClientPortalAuthProvider>
              <ClientSOWRedline />
            </ClientPortalAuthProvider>
          }
        />
        <Route
          path="/client/agreement/:token"
          element={
            <ClientPortalAuthProvider>
              <ClientAgreementView />
            </ClientPortalAuthProvider>
          }
        />
        {/* Public verification route */}
        <Route path="/verify/:token" element={<AgreementVerify />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <GlobalErrorFallback error={error} resetError={resetError} />
    )}
    showDialog={false}
  >
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <AuthQueryBridge />
        <AppSessionProvider>
          <ThemeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <PostHogProvider>
                  <DemoModeProvider>
                    <DashboardFilterProvider>
                      <SensitiveDataRegistryProvider>
                        <BidCelebrationProvider>
                          <AppRoutes />
                        </BidCelebrationProvider>
                      </SensitiveDataRegistryProvider>
                    </DashboardFilterProvider>
                  </DemoModeProvider>
                </PostHogProvider>
              </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </AppSessionProvider>
      </QueryClientProvider>
    </AuthProvider>
  </Sentry.ErrorBoundary>
);

export default App;
